import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import {
  getFinancialYear,
  GetFinancialYearQuery,
  GetFinancialYearQueryVariables,
  listFinancialYear,
  ListFinancialYearQuery,
  ListFinancialYearQueryVariables,
  listUserPreference,
  ListUserPreferenceQuery,
} from '../../graphql';
import { PURGE } from 'redux-persist';
import { setSwitchVertical } from './DashboardSlice';
import dayjs from 'dayjs';

// Type definitions
type UserPreference = NonNullable<NonNullable<ListUserPreferenceQuery['listUserPreference']>[number]>;
type BaseFinancialYear = NonNullable<NonNullable<GetFinancialYearQuery['getFinancialYear']>[0]>;
type FinancialYear = BaseFinancialYear & {
  startdate: number;
  enddate: number;
};
type FinancialYearList = NonNullable<NonNullable<ListFinancialYearQuery['listFinancialYear']>[number]>;

export interface FetchUserPreferenceReturn {
  userPreference: UserPreference[];
  currentFY: FinancialYear;
  listFY: FinancialYearList[];
}

export const ListUserPreference = async (): Promise<GraphQLResult<ListUserPreferenceQuery>> => {
  const result = (await API.graphql(graphqlOperation(listUserPreference))) as GraphQLResult<ListUserPreferenceQuery>;
  return result;
};

export const GetFinancialYear = async (variables: GetFinancialYearQueryVariables): Promise<GraphQLResult<GetFinancialYearQuery>> => {
  const result = (await API.graphql(graphqlOperation(getFinancialYear, variables))) as GraphQLResult<GetFinancialYearQuery>;
  return result;
};

export const ListFinancialYear = async (variables: ListFinancialYearQueryVariables): Promise<GraphQLResult<ListFinancialYearQuery>> => {
  const result = (await API.graphql(graphqlOperation(listFinancialYear))) as GraphQLResult<ListFinancialYearQuery>;
  return result;
};

export const fetchUserPreference = createAsyncThunk<FetchUserPreferenceReturn, void>(
  'userPreference/fetch',
  async (arg, { getState, dispatch }) => {
    try {
      const response = await ListUserPreference();
      const FYObj = response?.data?.listUserPreference?.find((fy) => fy?.attributetypecd === 'SelectedFinYear');
      if (!FYObj?.attributevalue) {
        throw new Error('Financial Year not found in user preferences');
      }
      const FYresponse = await GetFinancialYear({ financialyearid: FYObj?.attributevalue });

      const listFYres = await ListFinancialYear({});
      const listUserPreferenceDta = response?.data?.listUserPreference;

      listUserPreferenceDta?.map((obj) => {
        if (obj) {
          const { attributetypecd, attributevalue } = obj;
          if (attributetypecd === 'profitNLoss' || attributetypecd === 'balanceSheet') {
            const value = attributevalue === 1 ? true : false;
            dispatch(setSwitchVertical({ outerKey: attributetypecd, value: value }));
          }
        }
      });
      let currentFY = FYresponse?.data?.getFinancialYear?.[0];
      if (currentFY && currentFY?.startdate && currentFY?.enddate) {
        currentFY.startdate = dayjs.unix(currentFY?.startdate).startOf('day').unix();
        currentFY.enddate = dayjs.unix(currentFY?.enddate).endOf('day').unix();
        return {
          userPreference: (listUserPreferenceDta || []).filter((item): item is UserPreference => item !== null),
          currentFY: currentFY as FinancialYear,
          listFY: (listFYres?.data?.listFinancialYear || []).filter((item): item is FinancialYearList => item !== null),
        };
      } else {
        throw new Error('Invalid financial year data');
      }
    } catch (error) {
      console.error('Error fetching user preference:', error);
      throw error;
    }
  }
);

// Initial state
interface UserPreferenceState {
  userPreference: UserPreference[];
  financialYear: FinancialYear | null;
  listFYData: FinancialYearList[];
  loading: boolean;
  render: boolean;
  error: string;
}

const INITIAL_STATE: UserPreferenceState = {
  userPreference: [],
  financialYear: null,
  listFYData: [],
  loading: false,
  render: true,
  error: '',
};

const userPrefSlice = createSlice({
  name: 'userPreferenceAndFY',
  initialState: INITIAL_STATE,
  reducers: {
    setInitialData: (state, action) => {
      const { currentFY, ListUserPreference, ListAllFinancialYears } = action.payload;

      state.financialYear = currentFY;
      state.listFYData = ListAllFinancialYears;
      state.userPreference = ListUserPreference;
    },
    setRemoveFY: (state, action) => {
      const fyIdToRemove = Number(action.payload);
      if (!fyIdToRemove) return;
      state.listFYData = state.listFYData.filter(
        (data) => Number(data?.financialyearid) !== fyIdToRemove
      );
    },
    setUserPreference: (state, action) => {
      state.userPreference = action.payload;
    },

    resetUserPreference: () => ({ ...INITIAL_STATE }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPreference.fulfilled, (state, action) => {
        state.userPreference = action.payload.userPreference;
        state.financialYear = action.payload.currentFY;
        state.listFYData = action.payload.listFY;
        state.loading = false;
        state.render = false;
      })
      .addCase(fetchUserPreference.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserPreference.rejected, (state, action) => {
        state.render = true;
        state.loading = false;
      })
      .addCase(PURGE, () => INITIAL_STATE);
  },
});

export const { setInitialData, resetUserPreference, setRemoveFY, setUserPreference } = userPrefSlice.actions;
export default userPrefSlice.reducer;
